import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Sparkles, CheckCircle2, XCircle, AlertTriangle,
  Car, User, Repeat, Wallet, Shield, Phone,
} from "lucide-react";

/**
 * Iter-148 · Phase-1 · Enhanced New-Lead sheet with:
 *   · Configurable-master preferences (manufacturer → model → variant, drive
 *     type, fuel/trans, terrain, colour, lead source)
 *   · Vehicle purchase category (First / Additional / Replacement) with
 *     conditional Exchange Info sub-form
 *   · Test-drive sub-section with DL upload + AI OCR (Gemini) + name-match
 *     verification (admin can flip strict/lenient in config)
 *   · Finance requirement + insurance preference
 *
 * Branch is auto-assigned by the backend from the logged-in user's primary
 * branch; salesperson defaults to the current user. Nothing to do on the UI.
 */
const PURCHASE_YEARS = (() => {
  const y = new Date().getFullYear();
  const out = [];
  for (let i = y; i >= y - 25; i--) out.push(String(i));
  return out;
})();

export default function LeadFormEnhanced({ open, onClose, onCreated }) {
  const [config, setConfig] = useState(null);
  const [masters, setMasters] = useState({
    manufacturer: [], model: [], variant: [],
    drive_type: [], fuel_transmission: [], terrain: [], colour: [], lead_source: [],
  });
  const [form, setForm] = useState(initialForm());
  const [dlBusy, setDlBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Load config + all master lists in parallel
    Promise.all([
      api.get("/dealership/config").catch(() => ({ data: {} })),
      ...["manufacturer","drive_type","fuel_transmission","terrain","colour","lead_source"]
        .map((lt) => api.get(`/dealership/masters/${lt}`, { params: { active_only: true } }).catch(() => ({ data: [] }))),
    ]).then(([cfgR, ...masterR]) => {
      setConfig(cfgR.data || {});
      const [manufacturer, drive_type, fuel_transmission, terrain, colour, lead_source] = masterR.map((r) => r.data || []);
      setMasters((m) => ({ ...m, manufacturer, drive_type, fuel_transmission, terrain, colour, lead_source }));
    });
  }, [open]);

  // Load models when manufacturer changes
  useEffect(() => {
    if (!form.preferred_manufacturer_id) { setMasters((m) => ({ ...m, model: [], variant: [] })); return; }
    api.get(`/dealership/masters/model`, {
      params: { parent_id: form.preferred_manufacturer_id, active_only: true },
    }).then((r) => setMasters((m) => ({ ...m, model: r.data || [] }))).catch(() => {});
  }, [form.preferred_manufacturer_id]);

  // Load variants when model changes
  useEffect(() => {
    if (!form.preferred_model_id) { setMasters((m) => ({ ...m, variant: [] })); return; }
    api.get(`/dealership/masters/variant`, {
      params: { parent_id: form.preferred_model_id, active_only: true },
    }).then((r) => setMasters((m) => ({ ...m, variant: r.data || [] }))).catch(() => {});
  }, [form.preferred_model_id]);

  const mustMobile = !!config?.lead_mandatory_mobile;
  const mustEmail  = !!config?.lead_mandatory_email;

  const dlOcr = async (file) => {
    if (!file) return;
    setDlBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams();
      const leadName = [form.first_name, form.last_name].filter(Boolean).join(" ").trim();
      if (leadName) params.set("lead_name", leadName);
      const r = await api.post(`/dealership/leads/dl-ocr?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ocr = r.data;
      setForm((f) => ({
        ...f,
        test_drive: {
          ...(f.test_drive || {}),
          requested: true,
          dl_upload: { filename: file.name, uploaded_at: new Date().toISOString() },
          dl_ocr: ocr,
          name_verification: ocr.name_verification || null,
        },
      }));
      const nv = ocr.name_verification;
      if (nv?.match) toast.success(`DL OCR ✓ · Name matches (${Math.round((nv.score||0)*100)}%)`);
      else if (nv) toast.warning(`DL OCR ⚠ · ${nv.reason}`, { duration: 8000 });
      else toast.info("DL OCR complete — no lead name to verify yet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DL OCR failed");
    } finally { setDlBusy(false); }
  };

  const save = async () => {
    // Local guards mirror backend rules — user gets instant feedback
    if (!form.first_name.trim()) return toast.error("First name is required");
    if (mustMobile && !form.mobile.trim()) return toast.error("Mobile is required by admin policy");
    if (mustEmail && !form.email.trim()) return toast.error("Email is required by admin policy");
    if (form.purchase_category === "replacement") {
      const xi = form.exchange_info || {};
      if (!xi.purchase_year || !xi.model) return toast.error("Exchange info: purchase year + model are required for Replacement leads");
    }
    // If DL name-verification failed and admin override is not configured / not requested, ask user
    const nv = form.test_drive?.name_verification;
    if (nv && !nv.match && !form.test_drive?.admin_override?.allowed) {
      const strict = nv.strict !== false;
      const ok = window.confirm(
        `DL name mismatch: ${nv.reason}\n\n` +
        (strict
          ? "Admin has enforced strict verification. Save anyway with override?"
          : "Continue saving? The mismatch will be logged on the lead."));
      if (!ok) return;
      setForm((f) => ({
        ...f,
        test_drive: { ...(f.test_drive || {}), admin_override: {
          allowed: true, by_user: "current_user", at: new Date().toISOString(),
          reason: "User bypassed name-mismatch warning on save",
        } },
      }));
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Push a Follow-up log entry so the lead has one right from creation.
      payload.follow_ups = [{
        at: new Date().toISOString(),
        by: "current_user",
        channel: form.first_contact_channel || "walk_in",
        note: form.notes || "Lead captured",
        outcome: "follow_up",
      }];
      const r = await api.post("/dealership/leads", payload);
      toast.success(`Lead ${r.data.lead_number} created`);
      onCreated?.(r.data);
      setForm(initialForm());
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create lead");
    } finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto" data-testid="lead-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent" />
            New Lead
          </SheetTitle>
          <SheetDescription className="text-[11px]">
            Branch auto-assigned from your hierarchy. Salesperson defaults to you.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 pb-24 mt-4">
          {/* ────── Section 1: Customer ────── */}
          <Section icon={<User className="w-3.5 h-3.5" />} title="Customer">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" required value={form.first_name}
                onChange={(v) => setForm({ ...form, first_name: v })} testid="lead-first-name" />
              <Field label="Last name" value={form.last_name}
                onChange={(v) => setForm({ ...form, last_name: v })} />
              <Field label="Mobile" required={mustMobile} value={form.mobile}
                onChange={(v) => setForm({ ...form, mobile: v })} testid="lead-mobile" />
              <Field label="Email" required={mustEmail} type="email" value={form.email}
                onChange={(v) => setForm({ ...form, email: v })} testid="lead-email" />
              <div className="col-span-2">
                <Label className="text-[11px]">Lead Source</Label>
                <MasterSelect items={masters.lead_source} value={form.lead_source_id}
                  onChange={(v) => setForm({ ...form, lead_source_id: v })}
                  placeholder="Where did we get this lead from?" testid="lead-source" />
              </div>
            </div>
          </Section>

          {/* ────── Section 2: Vehicle preference ────── */}
          <Section icon={<Car className="w-3.5 h-3.5" />} title="Vehicle preference">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px]">Manufacturer</Label>
                  <MasterSelect items={masters.manufacturer} value={form.preferred_manufacturer_id}
                    onChange={(v) => setForm({ ...form, preferred_manufacturer_id: v,
                      preferred_model_id: "", preferred_variant_id: "" })}
                    placeholder="Pick" testid="lead-manufacturer" />
                </div>
                <div>
                  <Label className="text-[11px]">Model</Label>
                  <MasterSelect items={masters.model} value={form.preferred_model_id}
                    onChange={(v) => setForm({ ...form, preferred_model_id: v, preferred_variant_id: "" })}
                    disabled={!form.preferred_manufacturer_id}
                    placeholder={form.preferred_manufacturer_id ? "Pick model" : "Pick manufacturer first"}
                    testid="lead-model" />
                </div>
                <div>
                  <Label className="text-[11px]">Variant</Label>
                  <MasterSelect items={masters.variant} value={form.preferred_variant_id}
                    onChange={(v) => setForm({ ...form, preferred_variant_id: v })}
                    disabled={!form.preferred_model_id}
                    placeholder={form.preferred_model_id ? "Pick variant" : "Pick model first"}
                    testid="lead-variant" />
                </div>
              </div>
              <div>
                <Label className="text-[11px]">Drive Type</Label>
                <MasterSelect items={masters.drive_type} value={form.preferred_drive_type_id}
                  onChange={(v) => setForm({ ...form, preferred_drive_type_id: v })} placeholder="e.g. FWD" />
              </div>
              <div>
                <Label className="text-[11px]">Fuel / Transmission</Label>
                <MasterSelect items={masters.fuel_transmission} value={form.preferred_fuel_transmission_id}
                  onChange={(v) => setForm({ ...form, preferred_fuel_transmission_id: v })} placeholder="e.g. Petrol · Automatic" />
              </div>
              <div>
                <Label className="text-[11px]">Terrain</Label>
                <MasterSelect items={masters.terrain} value={form.preferred_terrain_id}
                  onChange={(v) => setForm({ ...form, preferred_terrain_id: v })} placeholder="Where will it run?" />
              </div>
              <div>
                <Label className="text-[11px]">Preferred Colour</Label>
                <MasterSelect items={masters.colour} value={form.preferred_colour_id}
                  onChange={(v) => setForm({ ...form, preferred_colour_id: v })} placeholder="Pick colour" />
              </div>
            </div>
          </Section>

          {/* ────── Section 3: Purchase category (First / Additional / Replacement) ────── */}
          <Section icon={<Repeat className="w-3.5 h-3.5" />} title="Purchase category">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "first",       l: "First-time buyer",   sub: "Their first car" },
                { v: "additional",  l: "Additional",          sub: "Second/third car in family" },
                { v: "replacement", l: "Replacement",         sub: "Selling old car" },
              ].map((opt) => {
                const on = form.purchase_category === opt.v;
                return (
                  <button key={opt.v} type="button"
                    onClick={() => setForm({ ...form, purchase_category: opt.v,
                      // clear exchange if not replacement
                      exchange_info: opt.v === "replacement" ? (form.exchange_info || {}) : null,
                    })}
                    data-testid={`lead-pcat-${opt.v}`}
                    className={`text-left rounded-sm border p-2 transition-colors ${
                      on ? "border-accent bg-accent/10" : "border-border hover:border-accent/40"
                    }`}>
                    <div className="text-[12px] font-medium">{opt.l}</div>
                    <div className="text-[10px] text-muted-foreground">{opt.sub}</div>
                  </button>
                );
              })}
            </div>

            {form.purchase_category === "replacement" && (
              <div className="mt-3 border border-dashed border-accent/40 rounded-sm p-3 bg-accent/5 space-y-3" data-testid="lead-exchange-section">
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Exchange Info · Old Vehicle</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px]">Purchase Year *</Label>
                    <Select value={form.exchange_info?.purchase_year || ""}
                      onValueChange={(v) => setForm({ ...form, exchange_info: { ...(form.exchange_info || {}), purchase_year: v } })}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="lead-ex-year"><SelectValue placeholder="Year" /></SelectTrigger>
                      <SelectContent>{PURCHASE_YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px]">KMs driven *</Label>
                    <Input type="number" value={form.exchange_info?.kms_driven || ""}
                      onChange={(e) => setForm({ ...form, exchange_info: {
                        ...(form.exchange_info || {}),
                        kms_driven: e.target.value ? Number(e.target.value) : null,
                      } })}
                      className="rounded-sm mt-1 font-mono" data-testid="lead-ex-kms" placeholder="e.g. 45000" />
                  </div>
                  <Field label="Model *" value={form.exchange_info?.model || ""}
                    onChange={(v) => setForm({ ...form, exchange_info: { ...(form.exchange_info || {}), model: v } })}
                    testid="lead-ex-model" />
                  <Field label="Variant" value={form.exchange_info?.variant || ""}
                    onChange={(v) => setForm({ ...form, exchange_info: { ...(form.exchange_info || {}), variant: v } })} />
                  <Field label="Colour" value={form.exchange_info?.colour || ""}
                    onChange={(v) => setForm({ ...form, exchange_info: { ...(form.exchange_info || {}), colour: v } })} />
                  <Field label="Registration State" value={form.exchange_info?.reg_state || ""}
                    onChange={(v) => setForm({ ...form, exchange_info: { ...(form.exchange_info || {}), reg_state: v } })} placeholder="e.g. Maharashtra" />
                  <Field label="Registration City" value={form.exchange_info?.reg_city || ""}
                    onChange={(v) => setForm({ ...form, exchange_info: { ...(form.exchange_info || {}), reg_city: v } })} placeholder="e.g. Pune" />
                  <Field label="Registration Number (optional)" value={form.exchange_info?.reg_number || ""}
                    onChange={(v) => setForm({ ...form, exchange_info: { ...(form.exchange_info || {}), reg_number: v } })} placeholder="e.g. MH12AB1234" mono />
                </div>
              </div>
            )}
          </Section>

          {/* ────── Section 4: Test Drive with DL OCR ────── */}
          <Section icon={<Sparkles className="w-3.5 h-3.5" />} title="Test Drive (optional)">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.test_drive?.requested}
                onChange={(e) => setForm({ ...form, test_drive: { ...(form.test_drive || {}), requested: e.target.checked } })}
                data-testid="lead-td-requested" />
              Customer wants a test drive
            </label>

            {form.test_drive?.requested && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-[11px]">Driving Licence (required for test drive)</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <label className={`flex-1 border border-dashed rounded-sm px-3 py-2 cursor-pointer flex items-center gap-2 text-xs ${
                      dlBusy ? "opacity-50" : "hover:bg-muted/30"
                    }`}>
                      <Upload className="w-3.5 h-3.5 text-accent" />
                      <span className="flex-1">{form.test_drive?.dl_upload?.filename || "Upload DL photo or PDF"}</span>
                      <input type="file" accept="image/*,application/pdf" className="hidden"
                        disabled={dlBusy}
                        onChange={(e) => dlOcr(e.target.files?.[0])}
                        data-testid="lead-dl-upload" />
                    </label>
                    {dlBusy && <span className="text-[10px] font-mono text-accent animate-pulse">AI reading DL…</span>}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Powered by AI vision — we auto-extract DL number, name, and cross-verify against the lead name.
                  </div>
                </div>

                {form.test_drive?.dl_ocr && (
                  <div className="border rounded-sm p-2.5 bg-muted/20 space-y-1" data-testid="lead-dl-preview">
                    <div className="text-[10px] font-mono uppercase text-muted-foreground">Extracted from DL</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><b>DL#:</b> <span className="font-mono">{form.test_drive.dl_ocr.dl_number || "—"}</span></div>
                      <div><b>Name:</b> {form.test_drive.dl_ocr.name || "—"}</div>
                      {form.test_drive.dl_ocr.dob && <div><b>DOB:</b> {form.test_drive.dl_ocr.dob}</div>}
                      {form.test_drive.dl_ocr.expiry_date && <div><b>Valid till:</b> {form.test_drive.dl_ocr.expiry_date}</div>}
                    </div>
                    {form.test_drive?.name_verification && (
                      <div className={`mt-2 flex items-start gap-2 p-2 rounded-sm text-[11px] ${
                        form.test_drive.name_verification.match
                          ? "bg-emerald-500/10 text-emerald-700"
                          : "bg-red-500/10 text-red-600"
                      }`} data-testid="lead-dl-verify">
                        {form.test_drive.name_verification.match
                          ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          : <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
                        <div>
                          <div><b>{form.test_drive.name_verification.match ? "Name matches ✓" : "Name mismatch"}</b></div>
                          <div className="text-[10px] opacity-80">{form.test_drive.name_verification.reason}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* ────── Section 5: Finance requirement ────── */}
          <Section icon={<Wallet className="w-3.5 h-3.5" />} title="Finance requirement">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.finance_requirement?.required}
                onChange={(e) => setForm({ ...form, finance_requirement: {
                  ...(form.finance_requirement || {}),
                  required: e.target.checked,
                } })} data-testid="lead-finance-required" />
              Needs loan / finance
            </label>
            {form.finance_requirement?.required && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-[11px]">Amount (est.)</Label>
                  <Input type="number" value={form.finance_requirement?.amount_estimate || ""}
                    onChange={(e) => setForm({ ...form, finance_requirement: {
                      ...(form.finance_requirement || {}),
                      amount_estimate: e.target.value ? Number(e.target.value) : null,
                    } })}
                    className="rounded-sm mt-1 font-mono" placeholder="₹" data-testid="lead-finance-amount" />
                </div>
                <div>
                  <Label className="text-[11px]">Tenure (months)</Label>
                  <Input type="number" value={form.finance_requirement?.tenure_months || ""}
                    onChange={(e) => setForm({ ...form, finance_requirement: {
                      ...(form.finance_requirement || {}),
                      tenure_months: e.target.value ? Number(e.target.value) : null,
                    } })}
                    className="rounded-sm mt-1 font-mono" placeholder="e.g. 60" />
                </div>
                <div className="col-span-2">
                  <Field label="Preferred financier" value={form.finance_requirement?.financier_preference || ""}
                    onChange={(v) => setForm({ ...form, finance_requirement: { ...(form.finance_requirement || {}), financier_preference: v } })}
                    placeholder="e.g. HDFC / SBI / Bajaj / Any" />
                </div>
              </div>
            )}
          </Section>

          {/* ────── Section 6: Insurance preference ────── */}
          <Section icon={<Shield className="w-3.5 h-3.5" />} title="Insurance preference">
            <div className="grid grid-cols-4 gap-2">
              {[
                { v: "self",         l: "Self-arranged" },
                { v: "from_company", l: "From company" },
                { v: "other",        l: "Other broker" },
                { v: "not_decided",  l: "Not decided" },
              ].map((opt) => {
                const on = form.insurance_preference === opt.v;
                return (
                  <button key={opt.v} type="button"
                    onClick={() => setForm({ ...form, insurance_preference: opt.v })}
                    data-testid={`lead-ins-${opt.v}`}
                    className={`text-center rounded-sm border p-2 text-[11px] transition-colors ${
                      on ? "border-accent bg-accent/10 text-accent" : "border-border hover:border-accent/40"
                    }`}>{opt.l}</button>
                );
              })}
            </div>
          </Section>

          {/* Notes */}
          <Section icon={<Phone className="w-3.5 h-3.5" />} title="Follow-up + notes">
            <div>
              <Label className="text-[11px]">First contact channel</Label>
              <Select value={form.first_contact_channel || "walk_in"}
                onValueChange={(v) => setForm({ ...form, first_contact_channel: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="walk_in">Walk-in</SelectItem>
                  <SelectItem value="call">Phone call</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="visit">Field visit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-sm mt-1" rows={3} placeholder="Anything relevant — budget, timeline, hot points…" />
            </div>
          </Section>
        </div>

        <div className="fixed bottom-0 right-0 left-0 sm:left-auto sm:w-[42rem] bg-background border-t px-6 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1" data-testid="lead-save">
            {saving ? "Saving…" : "Save lead"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ────────────────────────────────────────────────────────────
// Small helpers
// ────────────────────────────────────────────────────────────
function Section({ icon, title, children }) {
  return (
    <div className="border rounded-sm p-3 space-y-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
        {icon}{title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid, placeholder, mono }) {
  return (
    <div>
      <Label className="text-[11px]">{label}{required && " *"}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className={`rounded-sm mt-1 ${mono ? "font-mono" : ""}`} data-testid={testid} placeholder={placeholder} />
    </div>
  );
}

function MasterSelect({ items, value, onChange, placeholder, disabled, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="rounded-sm mt-1" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {items.length === 0 && <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No options — add via Masters → Config Lists</div>}
        {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function initialForm() {
  return {
    first_name: "", last_name: "", mobile: "", email: "",
    lead_source_id: "", first_contact_channel: "walk_in",
    preferred_manufacturer_id: "", preferred_model_id: "", preferred_variant_id: "",
    preferred_drive_type_id: "", preferred_fuel_transmission_id: "",
    preferred_terrain_id: "", preferred_colour_id: "",
    purchase_category: "first",
    exchange_info: null,
    test_drive: null,
    finance_requirement: null,
    insurance_preference: "not_decided",
    notes: "",
  };
}
